import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ninjatype.css';
import icon from './assets/icon.png';
import utils from './utils';

import { BackgroundAnimation, AnimationSettings } from './assets/BackgroundAnimations';
const { easyContent, mediumContent, hardContent, sounds } = utils;

const NinjaType = () => {
  // Content arrays

  // State management
  const [content, setContent] = useState('');
  const [userInput, setUserInput] = useState('');
  const [timer, setTimer] = useState(30);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [stats, setStats] = useState({
    wpm: 0,
    accuracy: 100,
    correctChars: 0,
    totalChars: 0,
    skippedChars: 0
  });
  const [difficulty, setDifficulty] = useState('easy');
  const [theme, setTheme] = useState('ocean');
  const [showResults, setShowResults] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [textDisplayMode, setTextDisplayMode] = useState('inline');
  const [showSettings, setShowSettings] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [typingEffects, setTypingEffects] = useState({
    keySound: false,
    keySoundType: 'false',
    smoothCursor: true
  });

  const [wordsCount, setWordsCount] = useState(10000);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [wordPositions, setWordPositions] = useState([]);
  const [completedContent, setCompletedContent] = useState([]);
  const [currentParagraphIndex, setCurrentParagraphIndex] = useState(0);
  const [visibleWords, setVisibleWords] = useState([]);
  const [currentVisibleWordIndex, setCurrentVisibleWordIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });
  const [isTimeOver, setIsTimeOver] = useState(false);
  const [animationType, setAnimationType] = useState('flowers');
  const [lines, setLines] = useState([]);
  const [showThemeChanger, setShowThemeChanger] = useState(false);

  // Refs
  const idleTimeout = useRef(null);
  const inputRef = useRef(null);
  const textRef = useRef(null);
  const resultsRef = useRef(null);
  const audioContextRef = useRef(null);
  const wordContainerRef = useRef(null);
  const measurementElementRef = useRef(null);

  // Sound options
  const soundOptions = [
    { id: 'soundOff', name: 'Sound Off' },
    { id: 'mechanical', name: 'Mechanical Keyboard' },
    { id: 'typewriter', name: 'Typewriter' },
    { id: 'bubble', name: 'Bubble Pop' },
    { id: 'digital', name: 'Digital Beep' },
    { id: 'click', name: 'Soft Click' }
  ];

  // Theme options
  const themeOptions = [
    { id: 'midnight', name: 'Midnight' },
    { id: 'sunset', name: 'Sunset' },
    { id: 'forest', name: 'Forest' },
    { id: 'ocean', name: 'Ocean' },
    { id: 'light', name: 'Light' }
  ];

  // Get content based on difficulty
  const getContentByDifficulty = useCallback((currentDifficulty) => {
    switch (currentDifficulty) {
      case 'easy': return easyContent;
      case 'medium': return mediumContent;
      case 'hard': return hardContent;
      default: return easyContent;
    }
  }, []);

  // Load settings and history from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('ninjatype-settings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        setTheme(settings.theme || 'midnight');
        setDifficulty(settings.difficulty || 'easy');
        setTimer(settings.timer || 30);
        setWordsCount(settings.wordsCount || 10000);
        setTypingEffects(settings.typingEffects || {
          keySound: false,
          keySoundType: 'mechanical',
          smoothCursor: true
        });
        setAnimationType(settings.animationType || 'flowers');
      } catch (e) {
        console.error("Error parsing saved settings:", e);
      }
    }

    const savedHistory = localStorage.getItem('ninjatype-history');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Error parsing saved history:", e);
      }
    }
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    const settings = {
      theme,
      difficulty,
      timer,
      wordsCount,
      typingEffects,
      animationType
    };
    localStorage.setItem('ninjatype-settings', JSON.stringify(settings));
  }, [theme, difficulty, timer, wordsCount, typingEffects, animationType]);

  // Save history to localStorage
  useEffect(() => {
    localStorage.setItem('ninjatype-history', JSON.stringify(history));
  }, [history]);

  // Create measurement element on mount
  useEffect(() => {
    const measurementEl = document.createElement("div");
    measurementEl.id = "measurement-element";
    measurementEl.style.position = "absolute";
    measurementEl.style.visibility = "hidden";
    measurementEl.style.width = "100%";
    measurementEl.style.top = "-9999px";
    measurementEl.style.left = "-9999px";
    measurementEl.style.fontSize = "40px";
    measurementEl.style.lineHeight = "1.3";
    measurementEl.style.whiteSpace = "normal";
    measurementEl.style.wordBreak = "break-word";
    measurementEl.style.padding = "30px";
    measurementEl.style.fontFamily = "'Fira Code', 'Consolas', 'Monaco', monospace";
    document.body.appendChild(measurementEl);
    measurementElementRef.current = measurementEl;

    return () => {
      if (measurementElementRef.current && document.body.contains(measurementElementRef.current)) {
        document.body.removeChild(measurementElementRef.current);
      }
    };
  }, []);

  // Initialize content when difficulty or wordsCount changes
  useEffect(() => {
    generateNewContent();
  }, [difficulty, wordsCount, getContentByDifficulty]);

  // Calculate word positions when content changes
  useEffect(() => {
    if (content) {
      const words = content.split(" ");
      const positions = [];
      let index = 0;

      words.forEach((word) => {
        positions.push({
          start: index,
          end: index + word.length,
          word: word,
        });
        index += word.length + 1; // +1 for the space
      });

      setWordPositions(positions);

      // Split content into lines of exactly 9 words
      const newLines = [];
      for (let i = 0; i < words.length; i += 9) {
        newLines.push(words.slice(i, i + 9).join(" "));
      }
      setLines(newLines);
    }
  }, [content]);

  // Initialize first 3 lines
  useEffect(() => {
    if (!content || lines.length === 0) return;

    // Show first 3 lines
    const firstThreeLines = lines.slice(0, 3);
    setVisibleWords(firstThreeLines);
    setCurrentVisibleWordIndex(0);
  }, [content, lines]);

  useEffect(() => {
    if (!content || lines.length === 0 || !isTyping) return;

    const currentLineIndex = Math.floor(currentWordIndex / 9);

    // We want the user to always see 3 lines ahead of their current position
    if (
      currentLineIndex >= currentVisibleWordIndex + 2 &&
      currentVisibleWordIndex + 3 < lines.length
    ) {
      const newStartIndex = currentLineIndex - 1; // keep current line as middle
      const nextThreeLines = lines.slice(newStartIndex, newStartIndex + 3);

      setVisibleWords(nextThreeLines);
      setCurrentVisibleWordIndex(newStartIndex);
    }
  }, [currentWordIndex, content, lines, isTyping, currentVisibleWordIndex]);

  // Update current word index
  useEffect(() => {
    if (!userInput || wordPositions.length === 0) return;

    const currentCharIndex = userInput.length;
    const currentWordIdx = wordPositions.findIndex(
      pos => currentCharIndex >= pos.start && currentCharIndex <= pos.end
    );

    if (currentWordIdx !== -1 && currentWordIdx !== currentWordIndex) {
      setCurrentWordIndex(currentWordIdx);
    }
  }, [userInput, wordPositions, currentWordIndex]);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (isTyping && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && isTyping) {
      finishTypingTest();
    }
    return () => clearInterval(interval);
  }, [isTyping, timeLeft]);

  // Focus effect for results popup
  useEffect(() => {
    if (showResults && resultsRef.current) {
      resultsRef.current.focus();
    }
  }, [showResults]);

  // Initialize audio context for key sounds
  useEffect(() => {
    if (typingEffects.keySound && (window.AudioContext || window.webkitAudioContext)) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [typingEffects.keySound]);

  // Update cursor position when user types
  useEffect(() => {
    if (isTyping && wordContainerRef.current) {
      const updateCursor = () => {
        requestAnimationFrame(() => {
          const position = getCursorPosition();
          setCursorPosition(position);
        });
      };

      const timer = setTimeout(updateCursor, 10);
      return () => clearTimeout(timer);
    }
  }, [userInput, isTyping, currentVisibleWordIndex, wordPositions]);

  // Generate new content based on difficulty
  const generateNewContent = useCallback(() => {
    const contentArray = getContentByDifficulty(difficulty);
    const randomIndex = Math.floor(Math.random() * contentArray.length);
    let selectedContent = contentArray[randomIndex];

    // Limit content by word count
    const words = selectedContent.split(' ');
    if (words.length > wordsCount) {
      selectedContent = words.slice(0, wordsCount).join(' ');
    }

    setContent(selectedContent);
    setUserInput("");
    setCurrentWordIndex(0);
    setStats({ wpm: 0, accuracy: 100, correctChars: 0, totalChars: 0, skippedChars: 0 });
    setShowResults(false);
    setCompletedContent([]);
    setCurrentParagraphIndex(0);

    if (textRef.current) {
      textRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [difficulty, wordsCount, getContentByDifficulty]);

  const playKeySound = useCallback(() => {
    if (!typingEffects.keySound || !audioContextRef.current) return;

    try {
      switch (typingEffects.keySoundType) {
        case "mechanical":
        case "typewriter":
        case "bubble":
        case "digital":
        case "click": {
          const options = sounds[typingEffects.keySoundType];
          if (options && options.length > 0) {
            const audio = options[Math.floor(Math.random() * options.length)];
            if (audio && typeof audio.cloneNode === 'function') {
              const clone = audio.cloneNode();
              clone.volume = 0.8;
              clone.currentTime = 0;
              clone.play().catch(e => console.log("Audio play failed:", e));
            }
          }
          return;
        }
        case "soundOff":
        default:
          return;
      }
    } catch (err) {
      console.error("Key sound error:", err);
    }
  }, [typingEffects]);

  // Start the typing test
  const startTypingTest = useCallback(() => {
    setIsTyping(true);
    setIsTimeOver(false);
    setTimeLeft(timer);
    setShowResults(false);
    setUserInput('');
    setCurrentWordIndex(0);
    setStats({ wpm: 0, accuracy: 100, correctChars: 0, totalChars: 0, skippedChars: 0 });
    setStartTime(Date.now());
    setCompletedContent([]);
    setCurrentParagraphIndex(0);

    // Focus on input field
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  }, [timer]);

  // Get next paragraph when current one is completed
  const getNextParagraph = useCallback(() => {
    // Save current completed content
    setCompletedContent(prev => [...prev, { content, userInput }]);

    // Get the next paragraph index
    const contentArray = getContentByDifficulty(difficulty);
    const nextIndex = (currentParagraphIndex + 1) % contentArray.length;
    setCurrentParagraphIndex(nextIndex);

    // Get the next paragraph content
    let newContent = contentArray[nextIndex];

    // Limit content by word count
    const words = newContent.split(' ');
    if (words.length > wordsCount) {
      newContent = words.slice(0, wordsCount).join(' ');
    }

    setContent(newContent);
    setUserInput("");
    setCurrentWordIndex(0);
    setCurrentVisibleWordIndex(0);
  }, [difficulty, content, userInput, currentParagraphIndex, wordsCount, getContentByDifficulty]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setUserInput(value);

    // Start typing if not already started
    if (!isTyping && value.length > 0) {
      startTypingTest();
    }

    // Calculate stats
    calculateStats(value);

    // Play sound if enabled and character was added (not deleted)
    if (typingEffects.keySound && value.length > userInput.length) {
      playKeySound();
    }
  };

  // Handle key press events for hidden input
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ignore special keys like Shift, Ctrl, Alt
      if (e.key.length !== 1 && e.key !== "Backspace" && e.key !== " ") return;

      // Start typing test on first key
      if (!isTyping) {
        startTypingTest();
      }

      // Reset idle timer
      if (idleTimeout.current) clearTimeout(idleTimeout.current);
      idleTimeout.current = setTimeout(() => {
        setIsTyping(false);
        setShowResults(true);
      }, 3000);

      // Handle space key - skip to next word
      if (e.key === " ") {
        e.preventDefault();

        // Find the current word position
        if (currentWordIndex < wordPositions.length - 1) {
          const currentWord = wordPositions[currentWordIndex];
          const remainingChars = currentWord.end - userInput.length;

          // Count skipped characters for negative WPM calculation
          if (remainingChars > 0) {
            setStats(prev => ({
              ...prev,
              skippedChars: prev.skippedChars + remainingChars
            }));
          }

          // Skip to next word
          const nextWordStart = wordPositions[currentWordIndex + 1].start;
          setUserInput(userInput.padEnd(nextWordStart, '') + " ");
          setCurrentWordIndex(currentWordIndex + 1);
        }

        return;
      }

      // Handle backspace
      if (e.key === "Backspace") {
        // If we're at the start of a word, go back to previous word
        if (userInput.length > 0 &&
          userInput[userInput.length - 1] === ' ' &&
          currentWordIndex > 0) {
          setCurrentWordIndex(currentWordIndex - 1);
        }

        setUserInput((prev) => prev.slice(0, -1));
      } else {
        // Regular character input
        setUserInput((prev) => prev + e.key);
      }

      // Play sound + animation if active
      if (typingEffects.keySound) playKeySound();

      // Update stats
      calculateStats(userInput + (e.key === "Backspace" ? "" : e.key));
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      if (idleTimeout.current) clearTimeout(idleTimeout.current);
    };
  }, [isTyping, userInput, typingEffects, timer, currentWordIndex, wordPositions, playKeySound, startTypingTest]);

  // Check if current paragraph is completed
  useEffect(() => {
    if (isTyping && userInput.length === content.length && timeLeft > 0) {
      // Current paragraph is completed, get next one
      getNextParagraph();
    }
  }, [userInput, content, isTyping, timeLeft, getNextParagraph]);

  // Calculate typing statistics
  const calculateStats = useCallback((input) => {
    let correctChars = 0;

    for (let i = 0; i < input.length; i++) {
      if (input[i] === content[i]) {
        correctChars++;
      }
    }

    const totalChars = input.length;
    const accuracy = totalChars > 0 ? Math.round((correctChars / totalChars) * 100) : 100;

    // Calculate WPM (5 characters per word, minus skipped characters)
    const timeElapsed = timer - timeLeft;
    const minutes = timeElapsed / 60;

    // Calculate net characters (correct minus skipped)
    const netChars = Math.max(0, correctChars - stats.skippedChars);
    const wpm = minutes > 0 ? Math.round((netChars / 5) / minutes) : 0;

    setStats({ wpm, accuracy, correctChars, totalChars, skippedChars: stats.skippedChars });
  }, [content, timer, timeLeft, stats.skippedChars]);

  // Finish typing test
  const finishTypingTest = useCallback(() => {
    setIsTyping(false);
    setIsTimeOver(true);
    setShowResults(true);

    // Save to history
    const newResult = {
      id: Date.now(),
      date: new Date().toLocaleString(),
      wpm: stats.wpm,
      accuracy: stats.accuracy,
      correctChars: stats.correctChars,
      totalChars: stats.totalChars,
      skippedChars: stats.skippedChars,
      paragraphs: currentParagraphIndex + 1,
      difficulty,
      time: timer
    };

    setHistory(prev => [newResult, ...prev.slice(0, 9)]);
  }, [stats, currentParagraphIndex, difficulty, timer]);

  // Reset the test
  const resetTest = useCallback(() => {
    setIsTyping(false);
    setTimeLeft(0);
    setUserInput('');
    setCurrentWordIndex(0);
    setShowResults(false);
    generateNewContent();
  }, [generateNewContent]);

  // Handle keyboard events for the results popup
  const handleResultsKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (!isTyping) {
      startTypingTest();
    }
  }, [isTyping, startTypingTest]);

  useEffect(() => {
    const blockKeys = (e) => {
      if (showResults) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", blockKeys, true);
    return () => window.removeEventListener("keydown", blockKeys, true);
  }, [showResults]);

  // Share results
  const shareResults = useCallback(() => {
    if (navigator.share) {
      navigator.share({
        title: 'NinjaType Results',
        text: `I just typed at ${stats.wpm} WPM with ${stats.accuracy}% accuracy on NinjaType!`,
        url: window.location.href
      })
        .catch(() => {
          navigator.clipboard.writeText(
            `NinjaType Results: ${stats.wpm} WPM, ${stats.accuracy}% accuracy`
          );
          alert('Results copied to clipboard!');
        });
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(
        `NinjaType Results: ${stats.wpm} WPM, ${stats.accuracy}% accuracy`
      );
      alert('Results copied to clipboard!');
    }
  }, [stats]);

  const renderContent = useCallback(() => {
    return visibleWords.map((line, lineIndex) => {
      const globalLineIndex = currentVisibleWordIndex + lineIndex;
      const words = line.split(" ");

      return (
        <div key={globalLineIndex} className="text-line">
          {words.map((word, wordIndex) => {
            const globalWordIndex = (globalLineIndex * 9) + wordIndex;
            const wordStart = wordPositions[globalWordIndex]?.start || 0;

            return (
              <span
                key={globalWordIndex}
                className={`word ${globalWordIndex === currentWordIndex ? 'current-word' : ''}`}
              >
                {word.split('').map((char, charIndex) => {
                  const absoluteIndex = wordStart + charIndex;
                  let className = '';

                  if (absoluteIndex < userInput.length) {
                    className = userInput[absoluteIndex] === char ? 'correct' : 'incorrect';
                  }

                  if (absoluteIndex === userInput.length) {
                    className = 'current';
                  }

                  return <span key={charIndex} className={className}>{char}</span>;
                })}
                <span> </span>
              </span>
            );
          })}
        </div>
      );
    });
  }, [visibleWords, currentVisibleWordIndex, wordPositions, currentWordIndex, userInput]);

  const ReloadButton = useCallback(({ onReload }) => (
    <button
      className="reload-btn"
      onClick={onReload}
      aria-label="Reload test"
    >
      <svg xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="icon">
        <path d="M16 2h-2v2h2v2H4v2H2v5h2V8h12v2h-2v2h2v-2h2V8h2V6h-2V4h-2V2zM6 20h2v2h2v-2H8v-2h12v-2h2v-5h-2v5H8v-2h2v-2H8v2H6v2H4v2h2v2z" fill="#000000" />
      </svg>
    </button>
  ), []);

  const getCursorPosition = useCallback(() => {
    if (wordPositions.length === 0 || userInput.length === 0 || !wordContainerRef.current) {
      return { top: 0, left: 0 };
    }

    // Find the current character position
    const currentCharIndex = userInput.length;

    // Find which word contains this character
    const currentWordIndex = wordPositions.findIndex(
      pos => currentCharIndex >= pos.start && currentCharIndex <= pos.end
    );

    if (currentWordIndex === -1) {
      return { top: 0, left: 0 };
    }

    // Get the line index and word index within the line
    const lineIndex = Math.floor(currentWordIndex / 9);
    const wordIndexInLine = currentWordIndex % 9;

    // Get the line element
    const lineElements = wordContainerRef.current.querySelectorAll('.text-line');
    const visibleLineIndex = lineIndex - currentVisibleWordIndex;

    if (visibleLineIndex >= lineElements.length || visibleLineIndex < 0) {
      return { top: 0, left: 0 };
    }

    const lineElement = lineElements[visibleLineIndex];
    if (!lineElement) return { top: 0, left: 0 };

    // Get the word element
    const wordElements = lineElement.querySelectorAll('.word');
    if (wordIndexInLine >= wordElements.length) {
      return { top: 0, left: 0 };
    }

    const wordElement = wordElements[wordIndexInLine];
    if (!wordElement) return { top: 0, left: 0 };

    // Get the character position within the word
    const charIndexInWord = currentCharIndex - wordPositions[currentWordIndex].start;
    const charElements = wordElement.querySelectorAll('span');

    if (charIndexInWord < charElements.length && charIndexInWord >= 0) {
      const charElement = charElements[charIndexInWord];
      const rect = charElement.getBoundingClientRect();
      const containerRect = wordContainerRef.current.getBoundingClientRect();

      return {
        top: rect.top - containerRect.top,
        left: rect.left - containerRect.left
      };
    } else {
      // If at the end of the word, position after the last character
      if (charElements.length > 0) {
        const lastChar = charElements[charElements.length - 1];
        const rect = lastChar.getBoundingClientRect();
        const containerRect = wordContainerRef.current.getBoundingClientRect();

        return {
          top: rect.top - containerRect.top,
          left: rect.right - containerRect.left
        };
      }

      return { top: 0, left: 0 };
    }
  }, [wordPositions, userInput, currentVisibleWordIndex]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (!isTyping && userInput.length === 0) {
      startTypingTest();
    }
  }, [isTyping, userInput, startTypingTest]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // Toggle settings panel
  const toggleSettings = useCallback(() => {
    setShowSettings(!showSettings);
    setShowHistory(false);
    setShowThemeChanger(false);
  }, [showSettings]);

  // Toggle history panel
  const toggleHistory = useCallback(() => {
    setShowHistory(!showHistory);
    setShowSettings(false);
    setShowThemeChanger(false);
  }, [showHistory]);

  // Toggle theme changer
  const toggleThemeChanger = useCallback(() => {
    setShowThemeChanger(!showThemeChanger);
  }, [showThemeChanger]);

  // Change theme
  const changeTheme = useCallback((newTheme) => {
    setTheme(newTheme);
    setShowThemeChanger(false);
  }, []);


return (
  <div className={`ninjatype-app ${theme}-theme`}>
      <BackgroundAnimation type={animationType} />
    {/* Header (hidden while typing) */}
    {!isTyping && (
      <header className="app-header">
        <h1 className="title">
          <img src={icon} alt="Ninja Logo" width={40} className="header-logo" />
          NinjaType
        </h1>

        {/* Direct Controls */}
        <div className="quick-controls">
          <div className="control-btn">
            <select
              value={timer}
              onChange={(e) => setTimer(parseInt(e.target.value))}
              disabled={isTyping}
            >
              <option value={15}>15s</option>
              <option value={30}>30s</option>
              <option value={60}>1m</option>
              <option value={120}>2m</option>
              <option value={180}>3m</option>
              <option value={300}>5m</option>
              <option value={600}>10m</option>
            </select>
          </div>

          <div className="control-btn">
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              disabled={isTyping}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>

        {/* Header Actions */}
        <div className="header-actions">
          <button
            className={`header-btn ${showHistory ? 'active' : ''}`}
            onClick={toggleHistory}
            aria-label="View history"
          >
            <span className="icon">📊</span>
            History
          </button>
          <button
            className={`header-btn ${showSettings ? 'active' : ''}`}
            onClick={toggleSettings}
            aria-label="Open settings"
          >
            <span className="icon">⚙️</span>
            Settings
          </button>
        </div>
      </header>
    )}

    {/* History Panel (hidden while typing) */}
    {!isTyping && showHistory && (
      <div className="history-panel show">
        <div className="panel-header">
          <h3>Recent Results</h3>
          <button
            className="close-btn"
            onClick={() => setShowHistory(false)}
            aria-label="Close history"
          >
            &times;
          </button>
        </div>
        <div className="panel-content">
          {history.length > 0 ? (
            history.map(result => (
              <div key={result.id} className="history-item">
                <div className="history-stats">
                  <span className="history-wpm">{result.wpm} WPM</span>
                  <span className="history-accuracy">{result.accuracy}%</span>
                </div>
                <div className="history-meta">
                  <span className="history-difficulty">{result.difficulty}</span>
                  <span className="history-date">{result.date}</span>
                  <span className="history-paragraphs">{result.paragraphs} paragraphs</span>
                </div>
              </div>
            ))
          ) : (
            <p className="no-history">No history yet. Complete a typing test to see results here.</p>
          )}
        </div>
      </div>
    )}

    {/* Settings Panel (hidden while typing) */}
    {!isTyping && showSettings && (
      <div className="settings-panel show">
        <div className="panel-header">
          <h3>Settings</h3>
          <button className="close-btn" onClick={() => setShowSettings(false)}>
            &times;
          </button>
        </div>
        <div className="panel-content">
          <AnimationSettings
            animationType={animationType}
            setAnimationType={setAnimationType}
            showSettings={showSettings}
          />
          <div className="settings-group">
            <h4>Appearance</h4>
            <div className="setting-group">
              <label>Theme:</label>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
              >
                <option value="midnight">Midnight</option>
                <option value="sunset">Sunset</option>
                <option value="forest">Forest</option>
                <option value="ocean">Ocean</option>
                <option value="light">Light</option>
              </select>
            </div>
          </div>

          <div className="settings-group">
            <h4>Sound & Effects</h4>
            <div className="setting-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={typingEffects.keySound}
                  onChange={(e) =>
                    setTypingEffects({ ...typingEffects, keySound: e.target.checked })
                  }
                />
                Key Sound
              </label>
            </div>

            {typingEffects.keySound && (
              <div className="setting-group">
                <select
                  value={typingEffects.keySoundType}
                  onChange={(e) =>
                    setTypingEffects({ ...typingEffects, keySoundType: e.target.value })
                  }
                >
                  {soundOptions.map(sound => (
                    <option key={sound.id} value={sound.id}>{sound.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
{/* Typing Area (fixed) */}
<div className="typing-container fixed-area">
  <span className="time">{timeLeft}s</span>

  {/* Text Display stays fixed */}
  <div
    className={`text-display ${textDisplayMode}-mode`}
    ref={textRef}
  >
    <div className="words-container" ref={wordContainerRef}>
      {renderContent()}
      {isTyping && (
        <span
          className={`cursor ${typingEffects.smoothCursor ? 'smooth' : ''}`}
          style={{
            position: 'absolute',
            top: `${cursorPosition.top}px`,
            left: `${cursorPosition.left}px`
          }}
        ></span>
      )}
    </div>
  </div>

  {/* Hidden Input */}
  <input
    ref={inputRef}
    type="text"
    className="hidden-input"
    value={userInput}
    onChange={handleInputChange}
    onKeyDown={handleKeyDown}
    onFocus={handleFocus}
    onBlur={handleBlur}
    disabled={showResults || !isTyping || timeLeft === 0}
    autoFocus={true}
    inputMode="text"
    autoComplete="off"
    autoCorrect="off"
    autoCapitalize="off"
    spellCheck="false"
  />

  {/* Reload button stays fixed */}
  <div className="controls fixed-reload ">
    <ReloadButton onReload={resetTest} />
  </div>
</div>


    {/* Hint keys (hidden while typing) */}
    {!isTyping && (
      <div className="hint-keys ">
        <span><key>Reload </key> or <key>🔁</key> – Restart test</span>
      </div>
    )}

    {/* Theme Changer (hidden while typing) */}
    {!isTyping && (
      <div className="theme-changer-container">
        <button
          className="theme-changer-btn"
          onClick={toggleThemeChanger}
          aria-label="Change theme"
        >
          🎨
        </button>

        {showThemeChanger && (
          <div className="theme-options">
            {themeOptions.map(themeOption => (
              <button
                key={themeOption.id}
                className={`theme-option ${theme === themeOption.id ? 'active' : ''}`}
                onClick={() => changeTheme(themeOption.id)}
                aria-label={`Change to ${themeOption.name} theme`}
              >
                {themeOption.name}
              </button>
            ))}
          </div>
        )}
      </div>
    )}

    {/* Footer (hidden while typing) */}
    {!isTyping && (
      <footer className="footer">
        <div className="footer-bottom">
          <div className="footer-links">
            <a href="mailto:souran.g345@gmail.com">
              <i className="fas fa-envelope"></i> contact
            </a>
            <a href="https://n11home.vercel.app" target="_blank" rel="noreferrer noopener">
              <i className="fas fa-user"></i> about
            </a>
            <a href="https://github.com/amd5500gt" target="_blank" rel="noreferrer noopener">
              <i className="fas fa-code"></i> github
            </a>
            <a href="https://x.com/Jassspb03" target="_blank" rel="noreferrer noopener">
              <i className="fab fa-twitter"></i> twitter
            </a>
          </div>
        </div>
      </footer>
    )}

    {/* Results Overlay (unchanged) */}
    {showResults && (
      <div className="results-overlay" onClick={(e) => e.target === e.currentTarget && setShowResults(false)}>
        <div
          className="results-panel"
          ref={resultsRef}
          tabIndex={-1}
          onKeyDown={handleResultsKeyDown}
        >
          <button
            className="close-btn"
            onClick={() => setShowResults(false)}
            aria-label="Close results"
          >
            &times;
          </button>

          <div className="results-header">
            <h2>Typing Analysis Report</h2>
            <p>Detailed performance breakdown of your typing test</p>
          </div>

          <div className="results-content">
            <div className="performance-metrics">
              <div className="main-metric">
                <div className="metric-value">{stats.wpm}</div>
                <div className="metric-label">Words Per Minute</div>
                <div className="metric-comparison">
                  {stats.wpm > 60 ? "Excellent!" : stats.wpm > 40 ? "Good job!" : "Keep practicing!"}
                </div>
              </div>

              <div className="main-metric">
                <div className="metric-value">{stats.accuracy}%</div>
                <div className="metric-label">Accuracy</div>
                <div className="metric-comparison">
                  {stats.accuracy > 95 ? "Outstanding!" : stats.accuracy > 85 ? "Good!" : "Needs improvement"}
                </div>
              </div>
            </div>

            <div className="detailed-stats">
              <h3>Performance Breakdown</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Characters:</span>
                  <span className="stat-value">{stats.totalChars}</span>
                </div>

                <div className="stat-item">
                  <span className="stat-label">Correct Characters:</span>
                  <span className="stat-value">{stats.correctChars}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Time Duration:</span>
                  <span className="stat-value">{timer} s</span>
                </div>
              </div>
            </div>

            <div className="results-actions">
              <button className="action-btn primary" onClick={resetTest}>
                Try Again
              </button>
              <button className="action-btn secondary" onClick={shareResults}>
                Share Results
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

  </div>
);
}
export default NinjaType;
